import { renumberRows, type DraftRowShape, type RowValidator } from '@/lib/draft-rows/draftRow';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';

/** Each row's index by its normalized key; the first row with a key wins. */
export function indexByKey<TData>(
  rows: DraftRowShape<TData>[],
  keyOf: (data: TData) => string | undefined
): Map<string, number> {
  const index = new Map<string, number>();
  rows.forEach((row, i) => {
    const key = normalizedKey(keyOf(row.data));
    if (key && !index.has(key)) index.set(key, i);
  });
  return index;
}

/**
 * Merges imported rows into the rows on screen. An imported row matches an
 * on-screen row by key only and fills just that row's empty `fields`, so what
 * the admin sees or entered stays; an unmatched row appends. The n-th imported
 * row of a key matches the n-th on-screen row of that key; one with no row left
 * appends, so the duplicate check flags it and merging the same list again adds
 * nothing. Returns the current rows unchanged when nothing changed.
 */
export function fillGapsByKey<TData, TRow extends DraftRowShape<TData>>(
  current: TRow[],
  imported: TRow[],
  {
    keyOf,
    fields,
    validate
  }: {
    keyOf: (data: TData) => string | undefined;
    fields: readonly (keyof TData)[];
    validate: RowValidator<TData>;
  }
): TRow[] {
  const unmatchedByKey = new Map<string, number[]>();
  current.forEach((row, i) => {
    const key = normalizedKey(keyOf(row.data));
    if (key) unmatchedByKey.set(key, [...(unmatchedByKey.get(key) ?? []), i]);
  });
  const merged = [...current];
  const appended: TRow[] = [];
  for (const row of imported) {
    const index = unmatchedByKey.get(normalizedKey(keyOf(row.data)))?.shift();
    if (index === undefined) {
      appended.push(row);
      continue;
    }
    const onScreen = merged[index].data;
    const gaps = fields.filter((field) => !onScreen[field] && row.data[field]);
    if (gaps.length > 0) {
      const data = {
        ...onScreen,
        ...Object.fromEntries(gaps.map((field) => [field, row.data[field]]))
      };
      merged[index] = { ...merged[index], data, ...validate(data) };
    }
  }
  const changed = appended.length > 0 || merged.some((row, i) => row !== current[i]);
  return changed ? renumberRows([...merged, ...appended]) : current;
}
